import { Fragment, useState } from 'react';

import { ArrowDown, ChevronDown, MailCheck } from 'lucide-react';

import { Button } from 'components/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/Collapsible';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { LaneOutcome, LaneReasoning } from 'types/agents/QuickQuoteAgent';
import { cn } from 'utils/shadcn';

import { DataSources } from './DataSources';
import { FullReasoning } from './FullReasoning';
import {
  JudgeSummaryKind,
  findRule,
  formatDollars,
  formatPerMile,
  formatPercent,
  getConfidenceDisplay,
  getDataSources,
  groupRuleEvaluations,
  splitDollarAmounts,
  summarizeJudge,
} from './reasoningHelpers';

export type OpenInQuickQuoteHandler = (lane?: LaneReasoning) => void;

type LaneReasoningDetailsProps = {
  lane: LaneReasoning;
  onOpenInQuickQuote: OpenInQuickQuoteHandler;
  // Inside a batch: the batch-level action is primary, so this lane's action is quiet and
  // the draft note lives in the card header.
  isBatchLane?: boolean;
};

function ReasoningText({ text }: { text: string }) {
  return (
    <Typography variant='body-xs' className='leading-snug text-neutral-700'>
      {splitDollarAmounts(text).map((part, index) =>
        part.isAmount ? (
          <Typography
            key={index}
            as='span'
            variant='body-xs'
            weight='semibold'
            className='leading-[inherit]'
          >
            {part.text}
          </Typography>
        ) : (
          <Fragment key={index}>{part.text}</Fragment>
        )
      )}
    </Typography>
  );
}

// A quiet line under the summary: a note from Kit, not an alert.
function MutedNote({
  children,
  testId,
}: {
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <Typography
      variant='body-xs'
      textColor='muted'
      className='text-[11px] leading-snug'
      data-testid={testId}
    >
      {children}
    </Typography>
  );
}

// Why Kit held a needs-review lane: the flags, else the last double-check's open issues.
const reviewReasons = (lane: LaneReasoning): string[] => {
  const reasons = [...(lane.flags ?? [])];
  const judge = summarizeJudge(lane);
  if (judge.kind === JudgeSummaryKind.Issues && reasons.length === 0) {
    (judge.verdict.issues ?? []).forEach((issue) =>
      reasons.push(issue.problem)
    );
  }
  return reasons;
};

type KeyNumber = {
  label: string;
  value: string;
  valueClassName?: string;
  title?: string;
};

const GRID_COLUMNS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
};

// Sell / Buy / Margin / Confidence as a compact row of small stats.
function KeyNumbers({ items }: { items: KeyNumber[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <dl
      className={cn(
        'grid w-full gap-x-2 rounded-[4px] bg-neutral-100 px-2 py-1.5 tabular-nums',
        GRID_COLUMNS[items.length] ?? 'grid-cols-4'
      )}
      data-testid='agent-key-numbers'
    >
      {items.map((item) => (
        <div key={item.label} className='min-w-0' title={item.title}>
          <Typography
            as='dt'
            variant='body-xxs'
            textColor='muted'
            className='leading-normal'
          >
            {item.label}
          </Typography>
          <Typography
            as='dd'
            variant='body-xs'
            weight='semibold'
            className={cn(
              'truncate text-[12.5px] leading-snug',
              item.valueClassName
            )}
          >
            {item.value}
          </Typography>
        </div>
      ))}
    </dl>
  );
}

const keyNumbersForLane = (lane: LaneReasoning): KeyNumber[] => {
  const items: KeyNumber[] = [];
  const hasMiles = lane.miles !== undefined && lane.miles > 0;
  if (lane.sellRate !== undefined) {
    items.push({
      label: 'Sell',
      value: formatDollars(lane.sellRate),
      title: hasMiles
        ? formatPerMile(lane.sellRate, lane.miles as number)
        : undefined,
    });
  }
  if (lane.buyRate !== undefined) {
    items.push({ label: 'Buy', value: formatDollars(lane.buyRate) });
  }
  if (lane.marginPct !== undefined) {
    items.push({
      label: 'Margin',
      value: formatPercent(lane.marginPct),
      valueClassName: 'text-success-600',
    });
  }
  const confidence = getConfidenceDisplay(lane.confidence);
  // Confidence alone (a no-bid with no rates) isn't worth a strip.
  if (confidence && items.length > 0) {
    items.push({
      label: 'Confidence',
      value: confidence.label,
      valueClassName: confidence.className,
    });
  }
  return items;
};

// One lane: the rates, how Kit priced them (full reasoning below), data sources, next step.
export function LaneReasoningDetails({
  lane,
  onOpenInQuickQuote,
  isBatchLane = false,
}: LaneReasoningDetailsProps) {
  const [isFullReasoningOpen, setIsFullReasoningOpen] = useState(false);

  if (lane.outcome === LaneOutcome.Error) {
    return (
      <Flex direction='col' gap='sm' className='w-full'>
        <Typography variant='body-xs' className='leading-snug text-neutral-700'>
          {lane.errorMessage ||
            'Kit couldn’t finish this one. You can quote it in Quick Quote below.'}
        </Typography>
        <Button
          type='button'
          variant='secondary'
          size='sm'
          className='h-8 w-fit text-[12px]'
          buttonNamePosthog='qq_agent_written_rules_run_manually'
          onClick={() => onOpenInQuickQuote(lane)}
        >
          Quote it in Quick Quote
        </Button>
      </Flex>
    );
  }

  const { applied } = groupRuleEvaluations(lane);
  const checkedCount = lane.ruleEvaluations?.length ?? 0;
  const noBidRule =
    lane.outcome === LaneOutcome.NoBid
      ? findRule(lane, lane.noBidRuleId)
      : null;
  const isNeedsReview = lane.outcome === LaneOutcome.NeedsReview;
  const flags = lane.flags ?? [];
  const notes = isNeedsReview ? reviewReasons(lane) : flags;

  return (
    <Flex direction='col' gap='sm' className='w-full'>
      <KeyNumbers items={keyNumbersForLane(lane)} />

      <Flex direction='col' gap='xs' className='w-full'>
        <Typography
          variant='body-xxs'
          weight='semibold'
          textColor='muted'
          className='uppercase leading-normal tracking-wide'
        >
          How Kit priced this
        </Typography>
        {lane.shortReasoning && <ReasoningText text={lane.shortReasoning} />}
        {noBidRule && (
          <MutedNote testId='agent-no-bid-reason'>
            Your rule: {noBidRule.ruleText}
          </MutedNote>
        )}
        {notes.length > 0 && (
          <MutedNote testId='agent-notes'>
            {isNeedsReview ? 'Kit held the draft. ' : 'Note: '}
            {notes.join(' ')}
          </MutedNote>
        )}
        <Collapsible
          open={isFullReasoningOpen}
          onOpenChange={setIsFullReasoningOpen}
          className='w-full'
        >
          <Flex align='center' justify='between' gap='sm' className='w-full'>
            {checkedCount > 0 ? (
              <MutedNote>
                Used {applied.length} of {checkedCount} rules
              </MutedNote>
            ) : (
              <span />
            )}
            <CollapsibleTrigger
              className='flex shrink-0 cursor-pointer items-center gap-0.5 whitespace-nowrap rounded-sm text-[11px] font-medium text-brand-main hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand'
              data-testid='agent-full-reasoning-toggle'
            >
              {isFullReasoningOpen
                ? 'Hide full reasoning'
                : 'Show full reasoning'}
              <ChevronDown
                className={cn(
                  'h-3 w-3 transition-transform',
                  isFullReasoningOpen && 'rotate-180'
                )}
                aria-hidden
              />
            </CollapsibleTrigger>
          </Flex>
          <CollapsibleContent>
            <FullReasoning lane={lane} />
          </CollapsibleContent>
        </Collapsible>
      </Flex>

      <DataSources sources={getDataSources(lane)} />

      {lane.outcome === LaneOutcome.Quote && !isBatchLane && (
        <Flex align='center' gap='xs' data-testid='agent-draft-hint'>
          <MailCheck
            className='h-3 w-3 shrink-0 text-neutral-500'
            aria-hidden
          />
          <MutedNote>Draft reply is in your Drumkit folder</MutedNote>
        </Flex>
      )}

      {isBatchLane ? (
        <button
          type='button'
          onClick={() => onOpenInQuickQuote(lane)}
          data-testid='agent-quote-lane-only'
          className='w-fit cursor-pointer rounded-sm text-[11.5px] font-medium text-brand-main hover:text-brand-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand'
        >
          {lane.outcome === LaneOutcome.NoBid
            ? 'Quote this lane anyway'
            : 'Quote this lane only'}
        </button>
      ) : (
        <Button
          type='button'
          variant='outline'
          size='sm'
          className='h-8 w-full gap-1.5 border-neutral-400 text-[12px]'
          buttonNamePosthog={
            lane.outcome === LaneOutcome.NoBid
              ? 'qq_agent_written_rules_quote_anyway'
              : 'qq_agent_written_rules_open_in_quick_quote'
          }
          onClick={() => onOpenInQuickQuote(lane)}
        >
          {lane.outcome === LaneOutcome.NoBid
            ? 'Quote anyway'
            : 'Open in Quick Quote'}
          <ArrowDown className='h-3.5 w-3.5' aria-hidden />
        </Button>
      )}
    </Flex>
  );
}
